import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  FlaskConical,
  Globe,
  Factory,
  Award,
  Microscope,
  Truck,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import ProductCard from "@/components/ProductCard";
import FiveRangeSection from "@/components/FiveRangeSection";
import BrochureDownloadButton from "@/components/BrochureDownloadButton";
import CataloguesSection from "@/components/CataloguesSection";
import { api } from "@/lib/api";
import { CONTACT } from "@/lib/constants";
import { useEffect, useState } from "react";

const STATS = [
  { value: "20+", label: "Years of Veterinary Expertise" },
  { value: "50+", label: "Pharmaceutical-Grade Formulations" },
  { value: "18+", label: "Export Destinations" },
  { value: "100K+", label: "Veterinarians & Farms Served" },
];

const WHY_CHOOSE = [
  { icon: FlaskConical, title: "Pharma-Grade R&D", desc: "Dedicated R&D laboratory developing science-backed veterinary formulations with proven field efficacy." },
  { icon: Factory, title: "Modern Manufacturing", desc: "GMP-compliant liquid, powder and granulation lines built for scale, accuracy and pharmaceutical hygiene." },
  { icon: ShieldCheck, title: "Quality Assurance", desc: "In-house QC laboratory testing every raw material, in-process sample and finished batch against strict specs." },
  { icon: Globe, title: "Export Ready", desc: "Documentation, packaging and regulatory support for distributors and importers across 18+ countries." },
  { icon: Microscope, title: "Veterinary Science", desc: "Formulations developed in consultation with veterinarians, nutritionists and dairy/poultry specialists." },
  { icon: Truck, title: "Reliable Supply", desc: "Robust supply chain ensuring on-time, in-full delivery for institutional and private-label clients." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 6))).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Veterinary Pharmaceutical Manufacturer India | Animal Health Products | Forage"
        description="Forage Animal Health & Nutrition is a leading veterinary pharmaceutical manufacturer in India producing pharma-grade animal health products, liquid supplements, mineral premixes and feed formulations for distributors, veterinarians and exporters."
        path="/"
        rawTitle
      />

      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative bg-[#0B2545] overflow-hidden"
      >
        <div className="container-fr grid lg:grid-cols-2 min-h-[88vh]">
          {/* Left: copy */}
          <div className="relative flex items-center px-6 md:px-12 lg:px-20 py-20 lg:py-24">
            <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
            <div className="relative max-w-xl">
              <span className="overline text-[#2A9D8F] fade-up">
                Veterinary Pharmaceuticals & Animal Nutrition
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] mt-5 fade-up fade-up-delay-1">
                Innovating <span className="text-[#2A9D8F]">Animal Health</span> Through Science and Quality.
              </h1>
              <p className="text-base lg:text-lg text-slate-200/90 mt-6 leading-relaxed fade-up fade-up-delay-2">
                Forage is a Saharanpur-based veterinary pharmaceutical and
                animal nutrition manufacturer. We produce pharma-grade
                supplements, chelated minerals and feed formulations trusted by
                distributors, veterinarians and exporters across 18+ countries.
              </p>
              <div className="flex flex-wrap gap-3 mt-8 fade-up fade-up-delay-3">
                <Link to="/products" data-testid="hero-products-btn" className="btn-accent">
                  Explore Products
                  <ArrowRight size={18} className="ml-2" />
                </Link>
                <BrochureDownloadButton
                  className="bg-[#C8A14B] text-[#0B2545] px-6 py-3 rounded-md font-semibold hover:bg-[#b48d3a] transition-all hover:-translate-y-0.5 shadow-md"
                  label="Download Catalogue"
                  testId="hero-brochure-btn"
                />
                <Link to="/contract-manufacturing" data-testid="hero-contract-btn" className="btn-outline-white">
                  Contract Mfg
                </Link>
              </div>
              {/* Trust pills */}
              <div className="flex flex-wrap gap-2 mt-9 fade-up fade-up-delay-4">
                {["GMP Certified", "ISO 9001:2015", "WHO-GMP Compliant", "Export House"].map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur text-xs text-white/90"
                  >
                    <CheckCircle2 size={12} className="text-[#2A9D8F]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: manufacturing image */}
          <div className="relative min-h-[360px] lg:min-h-full">
            <img
              src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=1400&q=85"
              alt="Forage pharmaceutical manufacturing facility"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Subtle blue tint for cohesion with left panel */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/30 to-transparent lg:from-[#0B2545]/60 lg:via-transparent lg:to-transparent" />
            {/* Floating capacity card */}
            <div className="absolute bottom-6 right-6 hidden md:block bg-white text-[#0B2545] p-5 rounded-xl shadow-2xl max-w-[260px] fade-up fade-up-delay-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2A9D8F] font-semibold">Capacity</div>
              <div className="font-display text-2xl font-black mt-1 leading-tight">50,000+ L / month</div>
              <div className="text-xs text-slate-600 mt-1">GMP-compliant liquid manufacturing line, Saharanpur</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="container-fr px-6 md:px-12 lg:px-20 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label}`} className="text-center md:text-left">
              <div className="font-display text-4xl lg:text-5xl font-black text-[#0B2545]">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="overline">About Forage</span>
            <h2 className="h-section mt-3">
              A new standard in Indian veterinary pharma manufacturing.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              Headquartered in Saharanpur, Uttar Pradesh, Forage Animal Health &
              Nutrition combines pharmaceutical-grade manufacturing discipline
              with veterinary domain expertise. Our integrated facility houses
              liquid manufacturing, automated filling, granulation, packaging
              and a full QC laboratory under one roof.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              From concept to commercial launch, we partner with veterinary
              brands, livestock companies, exporters and institutional buyers
              to deliver formulations that perform in the field.
            </p>
            <Link to="/about" data-testid="overview-about-btn" className="btn-outline mt-8">
              Learn more about Forage
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <img src="https://images.pexels.com/photos/37650270/pexels-photo-37650270.jpeg" alt="Filling line" loading="lazy" className="rounded-xl object-cover h-72 w-full" />
            <img src="https://images.pexels.com/photos/8851634/pexels-photo-8851634.jpeg" alt="QC Lab" loading="lazy" className="rounded-xl object-cover h-72 w-full mt-10" />
            <img src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=900&q=80" alt="Production" loading="lazy" className="rounded-xl object-cover h-56 w-full -mt-10" />
            <img src="https://images.unsplash.com/photo-1687360479287-143073b2bdcb?auto=format&fit=crop&w=900&q=80" alt="Cattle" loading="lazy" className="rounded-xl object-cover h-56 w-full" />
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="section-padding bg-white">
        <div className="container-fr">
          <div className="max-w-3xl">
            <span className="overline">Our Portfolio</span>
            <h2 className="h-section mt-3">
              Five product families. One commitment to quality.
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl">
              From bio-available liquid supplements to chelated mineral blends
              and complete feed premixes — every Forage product is built on
              scientific formulation and pharmaceutical-grade manufacturing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {categories.map((c, i) => (
              <Link
                key={c.id}
                to={`/products?category=${c.id}`}
                data-testid={`category-card-${c.id}`}
                className="group p-7 bg-[#F8FAFC] border border-slate-200 rounded-xl card-lift fade-up"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-[#0B2545] text-[#2A9D8F] flex items-center justify-center mb-5 group-hover:bg-[#2A9D8F] group-hover:text-white transition-colors">
                  <FlaskConical size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0B2545]">
                  {c.name}
                </h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  {c.description}
                </p>
                <span className="inline-flex items-center text-[#2A9D8F] text-sm font-medium mt-5 group-hover:gap-2 gap-1 transition-all">
                  View products <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="overline">Featured Products</span>
              <h2 className="h-section mt-3">Trusted formulations.</h2>
            </div>
            <Link to="/products" data-testid="all-products-link" className="text-[#2A9D8F] font-medium hover:underline inline-flex items-center gap-2">
              View all products <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <ProductCard product={p} index={i} key={p.id} />
            ))}
          </div>
        </div>
      </section>

      <FiveRangeSection />

      {/* CATALOGUES */}
      <CataloguesSection
        title="Download our product catalogues."
        subtitle="Species-specific catalogues for Cattle, Poultry, Aqua and Pet — pack sizes, formulations and technical specifications, ready to share with your team."
        testIdPrefix="home-catalogue"
      />

      {/* MANUFACTURING PREVIEW */}
      <section className="section-padding bg-[#0B2545] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="overline text-[#2A9D8F]">Manufacturing Excellence</span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mt-3">
                Built for pharmaceutical-grade output.
              </h2>
              <p className="text-slate-300 mt-6 leading-relaxed">
                Our integrated Saharanpur facility houses liquid manufacturing,
                automated filling, granulation, packaging and a full QC
                laboratory — all designed around GMP and WHO-GMP principles.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-10">
                {[
                  ["50,000+ L", "Monthly liquid capacity"],
                  ["100+ MT", "Powder & granulation"],
                  ["6", "Production halls"],
                  ["24x7", "QC laboratory"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <div className="font-display text-3xl font-bold text-[#2A9D8F]">{v}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <Link to="/manufacturing" data-testid="mfg-link" className="btn-accent mt-10">
                Tour our facility <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=1200&q=80"
                alt="Production line"
                className="rounded-xl object-cover w-full h-[480px] shadow-2xl"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-[#0B2545] p-5 rounded-xl shadow-xl max-w-[220px] hidden md:block">
                <Award className="text-[#2A9D8F] mb-2" size={22} />
                <div className="font-display font-bold text-lg leading-tight">GMP + ISO + WHO-GMP</div>
                <div className="text-xs text-slate-500 mt-1">Triple-certified manufacturing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section-padding bg-white">
        <div className="container-fr">
          <div className="max-w-3xl">
            <span className="overline">Why Forage</span>
            <h2 className="h-section mt-3">
              The veterinary partner global buyers trust.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {WHY_CHOOSE.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-choose-${i}`}
                className="p-7 rounded-xl border border-slate-200 bg-white card-lift fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-lg bg-[#2A9D8F]/10 text-[#2A9D8F] flex items-center justify-center mb-5">
                  <w.icon size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0B2545]">{w.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="overline">Client Enquiry</span>
            <h2 className="h-section mt-3">
              Tell us what you need. We respond within 24 hours.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              Whether you're a distributor exploring our catalogue, a brand
              evaluating contract manufacturing, or a veterinarian sourcing
              specific formulations — our team is ready with the technical
              details, samples and documentation you need.
            </p>
            <div className="mt-8 space-y-4 text-slate-700">
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="text-[#2A9D8F] mt-1" size={18} />
                <span>Technical datasheets and certificates of analysis</span>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="text-[#2A9D8F] mt-1" size={18} />
                <span>Custom formulation, private label and export packaging</span>
              </div>
              <div className="flex gap-3 items-start">
                <CheckCircle2 className="text-[#2A9D8F] mt-1" size={18} />
                <span>Pan-India distributor and exporter onboarding</span>
              </div>
            </div>
            <div className="mt-10 p-5 rounded-xl bg-[#0B2545] text-white">
              <div className="text-xs uppercase tracking-wider text-[#2A9D8F]">Direct line</div>
              <a href={`tel:${CONTACT.phoneTel}`} className="font-display text-2xl mt-1 block hover:text-[#2A9D8F]">{CONTACT.phone}</a>
              <div className="text-sm text-slate-300 mt-1">{CONTACT.email}</div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="font-display text-2xl font-bold text-[#0B2545] mb-2">
              Send an enquiry
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Our business development team will respond personally.
            </p>
            <ContactForm testIdPrefix="home-contact" />
          </div>
        </div>
      </section>
    </>
  );
}
